import { useUnifiedCheckoutBasketList } from "../../../../hooks/api/v2/unified-checkout-basket/useUnifiedCheckoutBaskets";
import { useUnifiedCheckoutDraftOrders } from "../../../unifiedCheckout/hooks/useUnifiedCheckoutDraftOrders";
import { calculateUnifiedCheckoutOrderPricing } from "../../../unifiedCheckout/lib/calculateUnifiedCheckoutOrderPricing";
import { isPromotionalOrderItem } from "../../../stockList/categoryPage/Storefront/StorefrontPromotion/isPromotionalOrderItem";
import { twMerge } from "tailwind-merge";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  H2,
  H3,
  H5,
  H6,
  Spinner,
} from "@sellar-io/components";
import {
  BuyerProductGetResponseType,
  DiscoveryV2ListResponseType,
} from "@sellar-io/api-v2/types";
import {
  calculateDeliveryOffers,
  findProductFulfillmentTypeDeliveryCharge,
  formatCurrency,
} from "@sellar-io/common";
import { useShallow } from "zustand/shallow";
import { SpinnerLegacy, StockIndicator } from "../../../../components";
import { Alert } from "../../../../components/alert/Alert";
import { AvailableFromBadge } from "../../../../components/badge/AvailableFromBadge";
import { BadgePurple } from "../../../../components/badge/Badge";
import { MaxOrderQuantityBadge } from "../../../../components/badge/MaxOrderQuantityBadge";
import { OrderDiscountSummary } from "../../../../components/discountSummary/OrderDiscountSummary";
import {
  Dialog,
  DialogNavigation,
  DialogContent,
  DialogCTA,
} from "../../../../components/modal/Dialog";
import {
  SkeletonContainer,
  SkeletonText,
} from "../../../../components/skeleton/Skeleton";
import { useBasketContext, useSellarUser } from "../../../../hooks";
import { useProductForMarket } from "../../../../hooks/api/v2/product/useProductForMarket";
import { useSupplierStorefrontV2 } from "../../../../hooks/api/v2/supplier/useSupplierStorefrontV2";
import { useSellarCurrentBusiness } from "../../../../hooks/useSellarCurrentBusiness.shared";
import { useDistributionDeliveryEstimate } from "../../../../hooks/useDeliveryEstimates/useDistributionDeliveryEstimate";

import { StorefrontOrderRulesCard } from "../../../stockList/categoryPage/Storefront/StorefrontCards/StorefrontOrderRulesCard";
import { StorefrontProductCard } from "../../../stockList/categoryPage/Storefront/StorefrontLayout/StorefrontProductCard";
import { useStorefrontBuyerPostcode } from "../../../stockList/categoryPage/Storefront/lib/useStorefrontBuyerPostcode";
import { useStockListOrderSummaryProps } from "../../../stockList/useStockListOrderSummaryProps";
import { useDiscoveryV2SearchStore } from "../DiscoveryV2SearchStore";
import { DiscoveryDialogFulfillmentTypeInformation } from "./DiscoveryDialogFulfillmentTypeInformation";
import { DiscoveryProductDialogCta } from "./DiscoveryProductDialogCta";
import { DietaryDisplayList } from "../../../../components/dietaryDisplayList/DietaryDisplayList";
import { useProductAvailableFormats } from "~/hooks/useProductAvailableFormats";
import { useRemoteFeatureFlags } from "~/hooks/useRemoteFeatureFlags";
import { DiscoveryV2AddToBasketCta } from "../distribution/DiscoveryV2AddToBasketCta";

type DiscoveryV2ProductDialogProps = {
  source: "market" | "order";
  active: boolean;
  onDismiss: () => void;
  ParentId: number;
  ProductItemId: number;
  setBestPricePromiseDialogActive: (
    product?: BuyerProductGetResponseType,
  ) => void;
};

export const DiscoveryV2ProductDialog = (
  props: DiscoveryV2ProductDialogProps,
) => {
  return (
    <Dialog
      fullHeight={true}
      active={props.active}
      onDismiss={props.onDismiss}
      className="w-full max-w-[1000px] rounded-none md:rounded-lg"
    >
      <DiscoveryV2ProductChildDialogContent {...props} />
    </Dialog>
  );
};

const DiscoveryV2ProductChildDialogContent = ({
  source,
  ParentId,
  ProductItemId,
  onDismiss,
  setBestPricePromiseDialogActive,
}: DiscoveryV2ProductDialogProps) => {
  const { isDeliveredBySellar, setSearchValue } = useDiscoveryV2SearchStore(
    useShallow((state) => ({
      isDeliveredBySellar: source === "market" && state.isDeliveredBySellar,
      setSearchValue: state.setSearchValue,
    })),
  );
  const { business } = useSellarCurrentBusiness();
  const { user } = useSellarUser();
  const productQuery = useProductForMarket(ParentId, {
    enabled: !!ParentId,
  });

  const childProduct = productQuery.data?.Products.find(
    (product) => product.id === ProductItemId,
  );
  const { buyerPostcode } = useStorefrontBuyerPostcode();
  const { nextDeliveryDate, orderByDate } = useDistributionDeliveryEstimate({
    supplierId: productQuery.data?.SupplierId ?? 0,
    productIds: childProduct ? [childProduct.id] : [],
    buyerPostcode: buyerPostcode,
    enabled:
      isDeliveredBySellar && !!productQuery.data?.SupplierId && !!childProduct,
  });

  const productPriceTier = childProduct?.ProductPriceTier;

  const sdConsolidatedCheckoutEnabled = useRemoteFeatureFlags().isActive(
    "sdConsolidatedCheckout",
  );

  const canShowAddToBasket =
    sdConsolidatedCheckoutEnabled && isDeliveredBySellar;

  const supplierStorefrontQuery = useSupplierStorefrontV2(
    productQuery.data?.SupplierId ?? 0,
    {
      ...(isDeliveredBySellar && {
        fulfillmentType: "sellarDistribution" as const,
      }),
    },
    {
      enabled: !!productQuery.data?.SupplierId,
    },
  );

  const [{ order }] = useBasketContext();
  const basketsQuery = useUnifiedCheckoutBasketList();
  const productBasket =
    basketsQuery.data?.find((basket) =>
      basket.DraftOrders.some((draft) =>
        draft.Items.some((item) => item.ProductId === ProductItemId),
      ),
    ) ??
    basketsQuery.data?.find((basket) =>
      basket.DraftOrders.some(
        (draft) => draft.SupplierId === productQuery.data?.SupplierId,
      ),
    );
  const { orders: draftOrders } = useUnifiedCheckoutDraftOrders(
    canShowAddToBasket && productBasket ? [productBasket] : [],
  );
  const discountOrderItems =
    draftOrders
      .find((draft) => draft.SupplierId === productQuery.data?.SupplierId)
      ?.OrderItems?.filter((item) => !isPromotionalOrderItem(item)) ?? [];
  const activeDiscountIds = calculateUnifiedCheckoutOrderPricing({
    orderItems: discountOrderItems,
    storefront: supplierStorefrontQuery.data,
    vatPercentage: 0,
  }).appliedVolumeDiscounts.map(({ volumeDiscount }) => volumeDiscount.id);

  const deliveryCharge = childProduct
    ? (findProductFulfillmentTypeDeliveryCharge({
        fulfillmentTypeContainerDeliveryCharges:
          supplierStorefrontQuery.data?.FulfillmentType
            .FulfillmentTypeContainerDeliveryCharges,
        product: {
          ...childProduct,
          displayUnit: childProduct.unit ?? null,
        },
      })?.charge ?? 0)
    : 0;

  const isStorefrontInfoLoading = supplierStorefrontQuery.isPending;

  const supplierStorefront = supplierStorefrontQuery.data;

  const priceTierDiscounts =
    supplierStorefront?.VolumeDiscounts?.PriceTierDiscounts ?? [];

  const priceTierOrderRules =
    supplierStorefront?.FulfillmentType.OrderRules?.PriceTierOrderRules ??
    supplierStorefront?.OrderRules?.PriceTierOrderRules ??
    [];

  const minimumOrderPrice =
    supplierStorefront?.FulfillmentType.OrderRules?.minimumOrderPrice ??
    supplierStorefront?.OrderRules?.minimumOrderPrice ??
    0;

  const freeDeliveryBasketSubtotal =
    supplierStorefront?.FulfillmentType.freeDeliveryBasketSubtotal ?? null;

  const {
    apiValues: { price: orderTotal },
  } = useStockListOrderSummaryProps({
    order,
    deliveryChargeRules:
      supplierStorefront?.FulfillmentType.DeliveryChargeRules,
    deliveryCharge: supplierStorefront?.FulfillmentType.charge,
    fulfillmentTypeContainerDeliveryCharges:
      supplierStorefront?.FulfillmentType
        .FulfillmentTypeContainerDeliveryCharges,
    priceTierDiscounts,
    caseSizeRuleList: supplierStorefront?.CaseSizeRules.list ?? [],
    priceTierOrderRules,
    minimumOrderPrice,
    containerTypeDeliveryChargeRules:
      supplierStorefront?.FulfillmentType.ContainerTypeDeliveryChargeRules ??
      [],
    freeDeliveryBasketSubtotal,
  });

  return (
    <>
      <DialogNavigation
        title={
          productQuery.isPending
            ? "Loading..."
            : productQuery.isError
              ? "Something went wrong..."
              : (productQuery.data.name ?? "")
        }
        leftIcon={"CHEVRON_DOWN"}
        onClickLeftIcon={onDismiss}
      />
      <DialogContent
        className={
          productQuery.isPending
            ? "flex min-h-0 flex-1 items-center justify-center"
            : undefined
        }
      >
        {productQuery.isPending ? (
          <Spinner size="lg" />
        ) : productQuery.isError ? (
          <div>Something went wrong</div>
        ) : (
          <div className={"grid gap-4 md:grid-cols-2"}>
            <StorefrontProductCard
              supplier={productQuery.data.Supplier}
              item={productQuery.data}
              setBestPricePromiseDialogActive={() => {
                setBestPricePromiseDialogActive(productQuery.data);
              }}
            />
            <div className={"flex flex-col gap-4"}>
              {childProduct ? (
                <Card className={twMerge("bg-white", "border border-solid")}>
                  <CardHeader
                    className={twMerge("bg-white", "flex flex-col gap-2 p-4")}
                  >
                    <H2>{childProduct.name?.split(" • ")[1]}</H2>
                    {productPriceTier?.discountable === false && (
                      <H6>This item is not eligible for volume discounts.</H6>
                    )}
                    <div className="flex flex-col gap-2">
                      <div className={"flex gap-1"}>
                        <DietaryDisplayList
                          dietaryRequirements={{
                            VG: Boolean(childProduct.vegan),
                            GF: Boolean(childProduct.glutenFree),
                            LF: Boolean(childProduct.lactoseFree),
                          }}
                          mode="badge"
                        />
                        <StockIndicator
                          stock={childProduct.availableStock}
                          canBackorder={childProduct.canBackorder}
                        />
                        {!!childProduct.maxOrderQuantity && (
                          <MaxOrderQuantityBadge
                            maxQuantity={childProduct.maxOrderQuantity}
                          />
                        )}
                      </div>
                      {!!childProduct.availableFrom && (
                        <AvailableFromBadge
                          availableFrom={childProduct.availableFrom}
                        />
                      )}
                    </div>
                  </CardHeader>
                  <CardContent className={"flex flex-col gap-4 p-4"}>
                    <div className={"flex justify-between"}>
                      <H5>Unit price (ex. VAT)</H5>
                      <div className={"flex items-center gap-2"}>
                        {!productPriceTier ? (
                          <>
                            {business && user ? (
                              <H5>£ Pricing on request</H5>
                            ) : (
                              <>
                                <p>£</p>{" "}
                                <SkeletonContainer>
                                  <SkeletonText
                                    className={"h-[20px] w-[75px]"}
                                  />
                                </SkeletonContainer>
                              </>
                            )}
                          </>
                        ) : (
                          <>
                            {productPriceTier.fullPrice !==
                            productPriceTier.price ? (
                              <H6
                                className={"text-sm line-through select-text"}
                              >
                                {formatCurrency(productPriceTier.fullPrice)}
                              </H6>
                            ) : null}
                            <H3 className="select-text">
                              {formatCurrency(productPriceTier.price)}
                            </H3>
                          </>
                        )}
                      </div>
                    </div>
                    {canShowAddToBasket &&
                      productPriceTier &&
                      productQuery.isFetched && (
                        <div className="hidden md:block">
                          <DiscoveryV2AddToBasketCta
                            product={toDiscoveryListProduct(
                              productQuery.data,
                              childProduct,
                              nextDeliveryDate,
                              orderByDate,
                            )}
                            className="w-full"
                          />
                        </div>
                      )}
                    {!!deliveryCharge && (
                      <div className={"flex justify-between"}>
                        <H6>Item delivery charge</H6>
                        <H6>
                          {formatCurrency(
                            deliveryCharge,
                            productQuery.data.Supplier.currency,
                          )}
                        </H6>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ) : null}
              {supplierStorefrontQuery.isPending ? (
                <SpinnerLegacy />
              ) : supplierStorefrontQuery.isError ? (
                <Alert message={"Something went wrong..."} />
              ) : (
                <>
                  {priceTierDiscounts.length ? (
                    <Card
                      className={twMerge(
                        "bg-white",
                        "flex flex-col gap-2 border p-4",
                      )}
                    >
                      <BadgePurple icon={"TAG_MULTIPLE_OUTLINE"}>
                        Volume discounts
                      </BadgePurple>
                      <OrderDiscountSummary
                        discounts={priceTierDiscounts}
                        currency={productQuery.data.Supplier.currency}
                        hideInfo={true}
                        orderItems={
                          canShowAddToBasket ? discountOrderItems : undefined
                        }
                        activeDiscountIds={
                          canShowAddToBasket ? activeDiscountIds : undefined
                        }
                        caseSizeRuleList={
                          supplierStorefrontQuery.data.CaseSizeRules.list
                        }
                      />
                    </Card>
                  ) : null}

                  {supplierStorefront && (
                    <DiscoveryDialogFulfillmentTypeInformation
                      supplierStorefront={supplierStorefront}
                      isDeliveredBySellar={isDeliveredBySellar}
                      supplierId={productQuery.data.Supplier.id}
                      productItem={childProduct}
                    />
                  )}
                  {calculateDeliveryOffers.length && (
                    <StorefrontOrderRulesCard
                      className="snap-center rounded-md border"
                      supplierStorefront={supplierStorefrontQuery.data}
                      order={order}
                      orderTotal={orderTotal}
                    />
                  )}
                </>
              )}
              <AlsoAvailableInCard
                product={productQuery.data}
                childProduct={childProduct}
              />
            </div>
          </div>
        )}
      </DialogContent>
      {!!productQuery.data?.Supplier &&
        !isStorefrontInfoLoading &&
        (canShowAddToBasket ? (
          <DialogCTA className="flex-col gap-2 space-x-0 md:flex-row md:space-x-3">
            {childProduct && productPriceTier && productQuery.isFetched && (
              <div className="w-full md:hidden">
                <DiscoveryV2AddToBasketCta
                  product={toDiscoveryListProduct(
                    productQuery.data,
                    childProduct,
                    nextDeliveryDate,
                    orderByDate,
                  )}
                  className="w-full"
                />
              </div>
            )}
            <Button
              color="secondary"
              className="w-full"
              onClick={() => {
                setSearchValue(productQuery.data.Supplier.Business.name);
                onDismiss();
              }}
            >
              More from {productQuery.data.Supplier.Business.name}
            </Button>
          </DialogCTA>
        ) : (
          <DiscoveryProductDialogCta
            source={source}
            product={childProduct ?? productQuery.data}
            supplier={productQuery.data.Supplier}
            connection={
              supplierStorefrontQuery.data?.SupplierRetailerConnection
            }
          />
        ))}
    </>
  );
};

/**
 * Maps a BuyerProductGetResponseType (from the product detail query) plus a
 * child product into the DiscoveryV2ListResponseType shape expected by
 * DiscoveryV2AddToBasketCta.
 *
 * Fields not available in the buyer product response are defaulted. Delivery
 * dates come from the same live estimate used by the product fulfilment panel,
 * so items added from the dialog are grouped consistently with search results.
 */
const toDiscoveryListProduct = (
  fullProduct: BuyerProductGetResponseType,
  child: BuyerProductGetResponseType["Products"][number],
  earliestDeliveryDate: Date | null,
  earliestOrderCutoffDate: Date | null,
): DiscoveryV2ListResponseType[number] => ({
  id: child.id,
  name: child.name,
  stock: child.stock,
  availableStock: child.availableStock,
  canBackorder: child.canBackorder,
  availableFrom: child.availableFrom,
  containerType: child.containerType,
  price: child.ProductPriceTier?.price ?? null,
  earliestDeliveryDate: earliestDeliveryDate?.toISOString() ?? null,
  earliestOrderCutoffDate: earliestOrderCutoffDate?.toISOString() ?? null,
  matchingDeliveryDate: null,
  matchingOrderCutoffDate: null,
  priceIsFromConnection: false,
  Parent: {
    id: fullProduct.id,
    imageUrl: fullProduct.imageUrl,
    heroImageUrl: fullProduct.heroImageUrl,
    abv: fullProduct.abv,
    description: fullProduct.description,
    kind: fullProduct.kind,
    vegan: fullProduct.vegan,
    lactoseFree: fullProduct.lactoseFree,
    glutenFree: fullProduct.glutenFree,
    containsPreOrderProductItem: false,
  },
  Supplier: {
    id: fullProduct.Supplier.id,
    imageUrl: fullProduct.Supplier.imageUrl,
    urlId: fullProduct.Supplier.urlId,
    hasOptedOutSellarCredit: false,
    BusinessFeatureFlags: [],
    Business: {
      name: fullProduct.Supplier.Business.name,
      logoUrl: fullProduct.Supplier.Business.logoUrl,
    },
  },
});

const AlsoAvailableInCard = ({
  product,
  childProduct,
}: {
  product: BuyerProductGetResponseType;
  childProduct: BuyerProductGetResponseType["Products"][number] | undefined;
}) => {
  const otherContainers = useProductAvailableFormats(product, childProduct?.id);

  if (otherContainers.length === 0) {
    return null;
  }

  return (
    <Card className={twMerge("bg-white", "border p-4")}>
      <H5>Also Available in</H5>
      <div>{otherContainers.join(", ")}</div>
    </Card>
  );
};
